// src/config/api.js
const API = {
  BASE_URL: process.env.REACT_APP_API_BASE_URL || 'https://prod.drwaiting.com',
  ENDPOINTS: {
    PATIENTS: '/api/patients',
    DOCTOR_PATIENTS: (doctorId) => `/api/patients/doctor/${doctorId}`,
    HISTORY: '/api/history', // Use history endpoint which works correctly
    PATIENT_BY_ID: (patientId) => `/api/patients/${patientId}`,
    PATIENT_VITALS: (patientId) => `/api/patients/${patientId}/vitals`,
    ARCHIVE_PATIENT: (patientMongoId) => `/api/patients/${patientMongoId}/archive`,
    UPDATE_RECEIPT: (patientId, visitId, receiptId) => `/api/patients/${patientId}/visits/${visitId}/receipts/${receiptId}`,
    DELETE_RECEIPT: (patientId, visitId, receiptId) => `/api/patients/${patientId}/visits/${visitId}/receipts/${receiptId}`,
    UPDATE_VISIT_TYPE: (patientId) => `/api/patients/${patientId}/visit-type`,
    TRANSFER_PATIENT_VISIT: (patientMongoId) => `/api/patients/${patientMongoId}/transfer-visit`,
    DOCTOR_SETTINGS: (doctorId) => `/api/doctors/${doctorId}/settings`,
    DOCTOR_CHANGE_PASSWORD: (doctorId) => `/api/doctors/${doctorId}/change-password`,
    PRESCRIPTION_TEMPLATES: (doctorId) => `/api/doctors/${doctorId}/prescription-templates`,
    PRESCRIPTION_TEMPLATE: (doctorId, templateId) => `/api/doctors/${doctorId}/prescription-templates/${templateId}`,
    PHYSICAL_PRESCRIPTION_HTML: '/api/prescription-print/physical-html',
    ASSESSMENT_FORM_TEMPLATES: '/api/assessment-forms/templates',
    ASSESSMENT_FORM_RESPONSES: '/api/assessment-forms/responses',
    ASSESSMENT_FORM_RESPONSE: (assessmentId) => `/api/assessment-forms/responses/${assessmentId}`,

    // Services endpoints
    SERVICES: '/api/services',
    SERVICES_BATCH_ASSIGN: '/api/services/batch-assign',
    SERVICES_SYNC_PRICES: '/api/services/sync-prices',
    DOCTOR_SERVICES: (doctorId) => `/api/services/doctor/${doctorId}`,
    SERVICE: (doctorId, serviceId) => `/api/services/doctor/${doctorId}/${serviceId}`,

    // Billing endpoints
    BILLING: '/api/billing',
    RECORD_CONSULTATION: '/api/billing/consultation',  // Quick consultation record
    DOCTOR_BILLINGS: (doctorId) => `/api/billing/doctor/${doctorId}`,
    BILLING_RECORD: (doctorId, billingId) => `/api/billing/doctor/${doctorId}/${billingId}`,
    BILLING_BY_VISIT: (visitId) => `/api/billing/visit/${visitId}`,
    BILLING_UNPAID: (doctorId) => `/api/billing/doctor/${doctorId}/unpaid`,
    ADD_BILLING_PAYMENT: (doctorId, billingId) => `/api/billing/doctor/${doctorId}/${billingId}/payments`,
    DELETE_BILLING_PAYMENT: (doctorId, billingId, transactionId) => `/api/billing/doctor/${doctorId}/${billingId}/payments/${transactionId}`,

    // Analytics endpoints (legacy — kept for mobile apps and other consumers, untouched)
    REVENUE_OVERVIEW: (doctorId) => `/api/analytics/revenue/${doctorId}`,
    REVENUE_BREAKDOWN: (doctorId) => `/api/analytics/revenue/${doctorId}/breakdown`,
    SERVICES_ANALYTICS: (doctorId) => `/api/analytics/services/${doctorId}`,
    CLINIC_PERFORMANCE: (doctorId) => `/api/analytics/performance/${doctorId}`,
    PATIENT_BILLING_HISTORY: (doctorId, patientId) => `/api/analytics/patient/${doctorId}/${patientId}`,

    // NEW Revenue module — single source of truth (cash-basis, see utils/revenue.ts)
    REVENUE_DOCTOR:  (doctorId) => `/api/revenue/doctor/${doctorId}`,
    REVENUE_DOCTOR_BREAKDOWN: (doctorId) => `/api/revenue/doctor/${doctorId}/breakdown`,
    REVENUE_TODAY:   (doctorId) => `/api/revenue/doctor/${doctorId}/today`,
    REVENUE_HISTORY: (doctorId) => `/api/revenue/doctor/${doctorId}/history`,
    REVENUE_CLINIC:  (clinicId) => `/api/revenue/clinic/${clinicId}`,
    REVENUE_PATIENT: (doctorId, patientId) => `/api/revenue/patient/${doctorId}/${patientId}`,

    // Billing money-movement endpoints (idempotent via op_id; write BillingEvent log)
    BILLING_PAYMENT:        (doctorId, billingId) => `/api/billing/doctor/${doctorId}/${billingId}/payments`,
    BILLING_REFUND:         (doctorId, billingId) => `/api/billing/doctor/${doctorId}/${billingId}/refund`,
    BILLING_REFUND_DUE:     (doctorId, billingId) => `/api/billing/doctor/${doctorId}/${billingId}/refund-due`,
    BILLING_CONFIRM_REFUND: (doctorId, billingId) => `/api/billing/doctor/${doctorId}/${billingId}/confirm-refund`,
    BILLING_CANCEL:         (doctorId, billingId) => `/api/billing/doctor/${doctorId}/${billingId}/cancel`,
    BILLING_VOID:           (doctorId, billingId) => `/api/billing/doctor/${doctorId}/${billingId}/void`,
    BILLING_TIMELINE:       (doctorId, billingId) => `/api/billing/doctor/${doctorId}/${billingId}/timeline`,

    // External Services endpoints
    EXTERNAL_SERVICES: '/api/external-services/services',
    DOCTOR_EXTERNAL_SERVICES: (doctorId) => `/api/external-services/services/doctor/${doctorId}`,
    EXTERNAL_SERVICE: (doctorId, serviceId) => `/api/external-services/services/doctor/${doctorId}/${serviceId}`,
    EXTERNAL_REQUESTS: '/api/external-services/requests',
    DOCTOR_EXTERNAL_REQUESTS: (doctorId) => `/api/external-services/requests/doctor/${doctorId}`,
    PATIENT_EXTERNAL_REQUESTS: (patientId) => `/api/external-services/requests/patient/${patientId}`,
    EXTERNAL_REQUEST_STATUS: (requestId) => `/api/external-services/requests/${requestId}/status`,
    EXTERNAL_REQUEST_DELETE: (requestId) => `/api/external-services/requests/${requestId}`,
    EXTERNAL_REPORTS: (doctorId) => `/api/external-services/reports/doctor/${doctorId}`,

    // Medical History endpoints
    MEDICAL_HISTORY_TEMPLATE: '/api/medical-history/template',
    MEDICAL_HISTORY_PATIENT: '/api/medical-history/patient',
    MEDICAL_HISTORY_TIMELINE: '/api/medical-history/patient/timeline',

    // Visit Type Configuration endpoints
    VISIT_TYPE_CONFIG: (doctorId) => `/api/visit-types/${doctorId}`,
    VISIT_TYPE_CALCULATE_PRICE: (doctorId) => `/api/visit-types/${doctorId}/calculate-price`,

    // Medical Report endpoints
    MEDICAL_REPORTS: (patientId) => `/api/medical-reports/patient/${patientId}`,
    MEDICAL_REPORT_ITEM: (patientId, reportId) => `/api/medical-reports/patient/${patientId}/${reportId}`,

    // Referral endpoints
    REFERRALS: (patientId) => `/api/referrals/patient/${patientId}`,
    REFERRAL_ITEM: (patientId, referralId) => `/api/referrals/patient/${patientId}/${referralId}`,

    // Package / Offer endpoints
    PACKAGES: '/api/packages',
    DOCTOR_PACKAGES: (doctorId) => `/api/packages/doctor/${doctorId}`,
    PACKAGE: (doctorId, packageId) => `/api/packages/doctor/${doctorId}/${packageId}`,
    PATIENT_PACKAGES: (patientId) => `/api/patient-packages/patient/${patientId}`,
    PATIENT_ACTIVE_PACKAGES: (patientId) => `/api/patient-packages/patient/${patientId}/active`,
    ASSIGN_PATIENT_PACKAGE: '/api/patient-packages',
    USE_PACKAGE_SESSION: (patientPackageId) => `/api/patient-packages/${patientPackageId}/use-session`,

    // Medicine endpoints
    DOCTOR_MEDICINES: (doctorId) => `/api/medicines/doctor/${doctorId}`,
    MEDICINE: (doctorId, medicineId) => `/api/medicines/doctor/${doctorId}/${medicineId}`,

    // Medicine Hub endpoints (global catalog — admin-managed, doctor can import)
    MEDICINE_HUB_SPECIALTIES: '/api/medicine-hub/specialties',
    MEDICINE_HUB_ALL: '/api/medicine-hub/all',
    MEDICINE_HUB_IMPORT: (doctorId) => `/api/medicine-hub/import/${doctorId}`,

    // Expense endpoints
    EXPENSES: '/api/expenses',
    DOCTOR_EXPENSES: (doctorId) => `/api/expenses/doctor/${doctorId}`,
    EXPENSE: (doctorId, expenseId) => `/api/expenses/doctor/${doctorId}/${expenseId}`,
    EXPENSE_SUMMARY: (doctorId) => `/api/expenses/summary/${doctorId}`,
    EXPENSE_TREND: (doctorId) => `/api/expenses/trend/${doctorId}`,

    // Fixed Expense Template endpoints
    FIXED_EXPENSE_TEMPLATES: '/api/fixed-expenses/templates',
    DOCTOR_FIXED_TEMPLATES: (doctorId) => `/api/fixed-expenses/templates/${doctorId}`,
    FIXED_TEMPLATE: (doctorId, templateId) => `/api/fixed-expenses/templates/${doctorId}/${templateId}`,
    FIXED_EXPENSES_APPLY: '/api/fixed-expenses/apply',

    // Insurance endpoints
    INSURANCE_COMPANIES: (doctorId) => `/api/insurance/companies/doctor/${doctorId}`,
    INSURANCE_COMPANY: (doctorId, companyId) => `/api/insurance/companies/doctor/${doctorId}/${companyId}`,
    INSURANCE_COMPANIES_CREATE: '/api/insurance/companies',
    INSURANCE_BATCH_ASSIGN: '/api/insurance/companies/batch-assign',
    INSURANCE_REPORT: (doctorId) => `/api/insurance/report/doctor/${doctorId}`,
    INSURANCE_ANALYTICS: (doctorId) => `/api/insurance/analytics/doctor/${doctorId}`,
    INSURANCE_PRICE_LISTS: (clinicId, companyId) => `/api/insurance/clinics/${clinicId}/companies/${companyId}/price-lists`,
    INSURANCE_PRICE_LIST: (clinicId, companyId, year) => `/api/insurance/clinics/${clinicId}/companies/${companyId}/price-lists/${year}`,
    INSURANCE_PRICE_LIST_ITEMS: (clinicId, companyId, year) => `/api/insurance/clinics/${clinicId}/companies/${companyId}/price-lists/${year}/items`,
    INSURANCE_RESOLVE_PRICE: (clinicId, companyId) => `/api/insurance/clinics/${clinicId}/companies/${companyId}/resolve-price`,

    ATTENDANCE_ZONES: (doctorId) => `/api/attendance/zones/${doctorId}`,
    ATTENDANCE_ZONE: '/api/attendance/zones',
    ATTENDANCE_ZONE_DELETE: (doctorId, clinicId) =>
      `/api/attendance/zones/${doctorId}/${encodeURIComponent(clinicId)}`,
    ATTENDANCE_CHECK: '/api/attendance/check',
    ATTENDANCE_LOGS: (doctorId) => `/api/attendance/logs/${doctorId}`,
    ATTENDANCE_CORRECT_MISSING_CHECKOUT: (doctorId, clinicId, logId) =>
      `/api/attendance/${encodeURIComponent(doctorId)}/${encodeURIComponent(clinicId)}/logs/${encodeURIComponent(logId)}/correct-missing-checkout`,
    ATTENDANCE_PAYROLL: (doctorId) => `/api/attendance/payroll/${doctorId}`,
    ATTENDANCE_PAYROLL_UPSERT: '/api/attendance/payroll',
    DOCTOR_ATTENDANCE_STATUS: (doctorId) => `/api/doctor-attendance/status/${doctorId}`,
    DOCTOR_ATTENDANCE_CHECK: '/api/doctor-attendance/check',
    DOCTOR_ATTENDANCE_LOGS: '/api/doctor-attendance/logs',
    DOCTOR_ATTENDANCE_CORRECT_MISSING_CHECKOUT: (doctorId, clinicId, logId) =>
      `/api/doctor-attendance/${encodeURIComponent(doctorId)}/${encodeURIComponent(clinicId)}/logs/${encodeURIComponent(logId)}/correct-missing-checkout`,
    OWNER_VISIT_REPORT: (clinicId) => `/api/owner-reports/clinics/${clinicId}/visits`,

    // Doctor appointments calendar endpoints
    DOCTOR_APPOINTMENTS: (doctorId) => `/api/appointments/doctor/${doctorId}`,
    DOCTOR_APPOINTMENT: (doctorId, appointmentId) => `/api/appointments/doctor/${doctorId}/${appointmentId}`,
    DOCTOR_APPOINTMENT_REMINDERS: (doctorId) => `/api/appointments/doctor/${doctorId}/reminders/upcoming`,

    // Official Meta WhatsApp Cloud API endpoints
    WHATSAPP_SEND_PRESCRIPTION: (patientId) => `/api/whatsapp/send-prescription/${patientId}`,
    WHATSAPP_SEND_REFERRAL: (patientId) => `/api/whatsapp/send-referral/${patientId}`,
    WHATSAPP_SEND_MEDICAL_REPORT: (patientId) => `/api/whatsapp/send-medical-report/${patientId}`,
    WHATSAPP_SEND_UPLOADED_REPORT: (patientId) => `/api/whatsapp/send-uploaded-report/${patientId}`,
    WHATSAPP_SEND_EXTERNAL_SERVICES: (patientId) => `/api/whatsapp/send-external-services/${patientId}`,
    WHATSAPP_BROADCAST: '/api/whatsapp/broadcast',

    // Inventory consumption endpoints
    INVENTORY_BULK_DEDUCT: '/api/inventory/bulk-deduct',
    INVENTORY_CLINIC_HISTORY: (clinicId) => `/api/inventory/${clinicId}/all-history`,
    INVENTORY_LOW_STOCK: (clinicId) => `/api/inventory/${clinicId}/low-stock`,

    // Speech-to-text: Deepgram REST fallback
    TRANSCRIBE: '/api/transcribe',
    // Returns Deepgram key so browser connects directly to wss://api.deepgram.com (same as Deepgram website)
    TRANSCRIBE_KEY: '/api/transcribe-key',
    // OpenAI Whisper — post-recording full-file transcription ("offline" mode)
    WHISPER: '/api/whisper',

    // GPT transcript cleanup (OpenAI, proxied through backend — key never exposed to browser)
    GPT_CLEAN: '/api/gpt-clean',
  }
};

export default API;
